import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Ideas, Knowledge, Me } from "@/api/resources";
import { IdeaDecisions, ReopenIdea, ReturnedBadge } from "@/components/IdeaReturn";
import { AuthorLabel } from "@/components/AuthorLabel";
import { ConvertedIdeaBanner, IdeaWorkLinks } from "@/components/IdeaWork";
import { AttachmentsSection } from "@/components/AttachmentsSection";
import { CommentsSection } from "@/components/CommentsSection";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { IdeaStatusBadge } from "@/components/ui/Badge";
import type { IdeaStatus } from "@/api/types";
import { Spinner } from "@/components/ui/Spinner";
import { useBackButton } from "@/lib/telegram-ui";
import { Markdown } from "@/lib/markdown";

export function KnowledgeSourceView() {
  const { type = "", id = "" } = useParams();
  const source = useQuery({ queryKey: ["knowledge-source", type, id], queryFn: () => Knowledge.source(type, id) });
  const me = useQuery({ queryKey: ["me"], queryFn: Me.get });
  // Spec 015: the idea itself carries its version, reviewer and decision history.
  const idea = useQuery({ queryKey: ["ideas", "one", id], queryFn: () => Ideas.peek(id), enabled: type === "idea" && !!id });
  useBackButton("/knowledge");
  if (source.isLoading) return <Spinner label="Открываем источник…" />;
  if (!source.data || source.isError) return <Card className="space-y-3">
    <p role="alert">{source.error?.message || "Источник недоступен"}</p>
    <Button variant="secondary" onClick={() => void source.refetch()}>Повторить</Button>
  </Card>;
  const s = source.data;
  const converted = type === "idea" && idea.data?.status === "converted";
  return <div className="space-y-4">
    {converted && idea.data && <ConvertedIdeaBanner idea={idea.data} />}
    <Card className="space-y-2">
      <h1 className="text-cell-title font-medium break-words">{s.title}</h1>
      {type === "idea" && <p><AuthorLabel authorId={s.author_id} /></p>}
      {type === "idea" && s.status && <IdeaStatusBadge status={s.status as IdeaStatus} />}
      {s.is_shared !== null && <p className="text-meta text-hint">{s.is_shared ? "Общий материал" : "Личная заметка"}</p>}
      {s.status === "superseded" && <p className="text-meta text-hint">Историческая запись. Проверь актуальный метод или задачу перед применением.</p>}
      {s.status === "erroneous" && <p className="text-meta text-dstr">Подход отмечен как ошибочный. Причина указана в пометках.</p>}
      {s.updated_at && <p className="text-meta text-hint">{new Date(s.updated_at).toLocaleString("ru-RU")}</p>}
      {s.parent_method_id && <Link className="inline-flex min-h-touch items-center text-accent" to={`/knowledge/${s.parent_method_id}`}>Открыть метод и текущую версию</Link>}
      {s.parent_task_id && <Link className="inline-flex min-h-touch items-center text-accent" to={`/tasks/${s.parent_task_id}`}>Открыть задачу</Link>}
    </Card>
    {s.sections.map((section) => <Card key={section.label} className="space-y-2">
      <h2 className="text-meta text-hint">{section.label}</h2>
      <Markdown className="text-body" text={section.body_md} />
    </Card>)}
    {type === "idea" && idea.data && ((idea.data.decisions?.length ?? 0) > 0 || idea.data.status === "rejected" || idea.data.status === "withdrawn") && <Card className="space-y-3">
      <ReturnedBadge idea={idea.data} />
      <IdeaDecisions idea={idea.data} />
      <ReopenIdea idea={idea.data} myUserId={me.data?.id} />
    </Card>}
    {(type === "idea" || type === "note") && <>
      {type === "idea" && !converted && <IdeaWorkLinks ideaId={id} />}
      <AttachmentsSection parentType={type} parentId={id} />
      <CommentsSection parentType={type} parentId={id} myUserId={me.data?.id} />
    </>}
  </div>;
}
